import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Search,
  Share2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  listVideosForShare,
  createShareLink,
  revokeShareLink,
  regenerateShareLink,
  deleteShareLink,
  ShareFilter,
  VideoWithShareStatus
} from '@/services/shareVideoService';
import { toast } from 'sonner';
import { ShareVideoCardCompact, ShareVideoCardCompactSkeleton } from '@/components/share-video/ShareVideoCardCompact';
import { ShareKPISummary } from '@/components/share-video/ShareKPISummary';

const filterOptions: { value: ShareFilter; label: string }[] = [
  { value: 'all', get label() { return translateInterfaceText("Todos"); } },
  { value: 'active', get label() { return translateInterfaceText("Ativos"); } },
  { value: 'revoked', get label() { return translateInterfaceText("Desativados"); } }
];

export default function ShareVideoList() {
  useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ShareFilter>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['share-videos', search, filter],
    queryFn: () => listVideosForShare({ q: search || undefined, filter })
  });

  const createMutation = useMutation({
    mutationFn: createShareLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-videos'] });
      toast.success(translateInterfaceText("Link criado com sucesso!"));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    }
  });

  const revokeMutation = useMutation({
    mutationFn: revokeShareLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-videos'] });
      toast.success(translateInterfaceText("Link revogado com sucesso!"));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    }
  });

  const regenerateMutation = useMutation({
    mutationFn: regenerateShareLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-videos'] });
      toast.success(translateInterfaceText("Novo link gerado com sucesso!"));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: deleteShareLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-videos'] });
      toast.success(translateInterfaceText("Compartilhamento excluído com sucesso!"));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    }
  });

  const handleCopyLink = (video: VideoWithShareStatus) => {
    if (video.shareLink) {
      const url = `${window.location.origin}/s/${video.shareLink.code}`;
      navigator.clipboard.writeText(url);
      toast.success(translateInterfaceText("Link copiado!"));
    }
  };

  return (
    <Layout>
      <div className="bg-background">
        {/* Header */}
        <div className="max-w-[1240px] mx-auto px-6 md:px-8 lg:px-6 pt-4 pb-4">
          <div className="flex items-center gap-2.5">
            <Share2 className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />
            <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
              {translateInterfaceText("Vídeos compartilhados")}</h1>
          </div>
        </div>
        <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)', marginTop: '-3px' }} />

        <div className="max-w-[1240px] mx-auto px-6 md:px-8 lg:px-10 py-6">

          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
            {/* Search */}
            <div className="relative flex-1 max-w-[720px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={translateInterfaceText("Buscar vídeo…")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-10"
              />
            </div>

            {/* Segmented Control Filters */}
            <div className="flex items-center bg-muted/50 p-1 rounded-[10px] overflow-x-auto">
              {filterOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilter(opt.value)}
                  className={cn(
                    "h-8 px-4 text-[13px] font-medium rounded-[8px] whitespace-nowrap transition-all",
                    filter === opt.value
                      ? "bg-[#2563eb] text-[#fff] shadow-sm"
                      : "text-muted-foreground"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* KPI Summary - Always visible */}
          <ShareKPISummary videos={data?.videos ?? []} className="mb-4" />

          {/* Content */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <ShareVideoCardCompactSkeleton key={i} />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12 bg-card border rounded-xl">
              <p className="text-destructive mb-4">{translateInterfaceText("Erro ao carregar vídeos")}</p>
              <Button
                variant="outline"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['share-videos'] })}
              >
                {translateInterfaceText("Tentar novamente")}</Button>
            </div>
          ) : data.videos.length === 0 ? (
            <div className="text-center py-20 bg-card border rounded-xl">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Share2 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-foreground">
                {translateInterfaceText("Nenhum vídeo compartilhado ainda")}</h3>
              <p className="text-[13px] text-muted-foreground max-w-md mx-auto">
                {translateInterfaceText("Quando você compartilhar um vídeo, ele aparecerá aqui com links e métricas.")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {data.videos.map(video => (
                <ShareVideoCardCompact
                  key={video.id}
                  video={video}
                  onCopyLink={() => handleCopyLink(video)}
                  onCreateLink={() => createMutation.mutate(video.id)}
                  onRevoke={() => revokeMutation.mutate(video.id)}
                  onRegenerate={() => regenerateMutation.mutate(video.id)}
                  onDelete={() => deleteMutation.mutate(video.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
